import { FilterSelectField, Option } from '@/components/Form';
import { useFacetSelect } from '@/features/analyze/hooks/useFacetSelect';
import { useMenuPositioning } from '@/features/analyze/hooks/useMenuPositioning';
import { Z_INDEX } from '@/features/analyze/constants';
import {
  AllowedFacetConditionOperator,
  FacetKey,
} from '@/features/analyze/types';
import clsx from 'clsx';
import {
  ComponentProps,
  ComponentType,
  Fragment,
  ReactNode,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

type FacetSelectFieldProps<K extends FacetKey> = Omit<
  ComponentProps<typeof FilterSelectField<Option, true>>,
  'placeholder' | 'onChange' | 'value' | 'options' | 'formatOptionLabel'
> & {
  facetKey: K;
  modes: AllowedFacetConditionOperator<K>[];
  IconComponent?: ComponentType<{ className?: string }>;
  label?: ReactNode;
  labelColor: string;
  labelClassName?: string;
  backgroundColor?: string;
};

export function FacetSelectField<K extends FacetKey>({
  facetKey,
  modes,
  IconComponent,
  label,
  labelClassName,
  ...props
}: FacetSelectFieldProps<K>) {
  return (
    <div>
      <div
        className={clsx(
          'mb-0.5 flex items-center gap-0.5 text-xs font-semibold',
          labelClassName
        )}
      >
        {IconComponent && <IconComponent className="size-4 stroke-2" />}
        {label}
      </div>

      <div
        className="rounded-tr-xl border-2"
        style={{
          borderColor: props.labelColor,
        }}
      >
        {modes.map((mode, idx) => (
          <Fragment key={String(mode)}>
            <SingleFacetSelectField
              facetKey={facetKey}
              mode={mode}
              {...props}
            />
            {idx < modes.length - 1 && (
              <hr className="mx-2 border-rw-light-gray" />
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function SingleFacetSelectField<K extends FacetKey>({
  facetKey,
  mode,
  labelColor,
  backgroundColor,
  ...props
}: Omit<FacetSelectFieldProps<K>, 'modes'> & {
  mode: AllowedFacetConditionOperator<K>;
}) {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  const {
    options,
    selectedOptions,
    isLoading,
    onChangeSelectedFacets,
    handleMenuOpen,
    handleMenuClose,
  } = useFacetSelect(facetKey, mode);

  const { positioning } = useMenuPositioning(selectRef);

  const handleMenuOpenControlled = () => {
    handleMenuOpen();
    setIsMenuOpen(true);
  };

  const handleMenuCloseControlled = () => {
    handleMenuClose();
    setIsMenuOpen(false);
  };

  return (
    <div className="relative w-full" ref={selectRef}>
      <div
        translate="no"
        className="pointer-events-none absolute left-2 top-1/2 z-10 min-w-10 -translate-y-1/2 rounded-tr-md px-1.5 py-1 text-center text-xs font-semibold uppercase text-white"
        style={{
          backgroundColor: backgroundColor ?? labelColor,
        }}
      >
        {String(mode)}
      </div>

      <FilterSelectField
        {...props}
        placeholder={<span translate="no">{t('キーワードで絞り込み')}</span>}
        options={options}
        value={selectedOptions}
        onChange={onChangeSelectedFacets}
        onMenuOpen={handleMenuOpenControlled}
        onMenuClose={handleMenuCloseControlled}
        menuIsOpen={isMenuOpen}
        isLoading={isLoading}
        isSearchable={true}
        className="rounded-tr-xl text-black"
        menuShouldScrollIntoView={false}
        menuPlacement={positioning.placement}
        styleProps={{
          size: 'md',
          labelColor,
          valuePaddingLeft: 55,
          menuMaxHeight: positioning.maxHeight,
          menuPortalZIndex: Z_INDEX.FACET_SELECT_MENU_PORTAL,
          showDropdownIndicator: false,
          themedMultiValue: true,
        }}
        isMulti
      />
    </div>
  );
}
